/**
 * Filters undefined & null values in a way that is typescript safe.
 *
 * @example
 *  // expects (undefined | string)[] --> string[]
 * const arrUndefined: (void | undefined | null | string)[] = ['one', undefined, null, 'two'];
 * const arrString: string[] = arrUndefined.filter(filterUndefined); // ['one', 'two']
 */
export function filterEmpty<T>(x: T | undefined | null | void): x is T {
  return x !== undefined && x !== null;
}

/**
 * Filters falsy values in a way that is typescript safe.
 *
 * @example
 *  // expects (undefined | string)[] --> string[]
 * const arrUndefined: (void | undefined | null | false | string)[] = ['one', undefined, null, false, 'two'];
 * const arrString: string[] = arrUndefined.filter(filterUndefined); // ['one', 'two']
 */
export function filterFalsy<T>(x: T | undefined | null | void | false): x is T {
  return !!x;
}

export function setEqual(first: Set<unknown>, second: Set<unknown>): boolean {
  if (first.size !== second.size) {
    return false;
  }
  for (const item of first) {
    if (!second.has(item)) {
      return false;
    }
  }
  return true;
}

export function arraysEqual(a: unknown[], b: unknown[]) {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }

  // If you don't care about the order of the elements inside
  // the array, you should sort both arrays here.
  // Please note that calling sort on an array will modify that array.
  // you might want to clone your array first.

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * By using this in default switch case, we can enforce exhaustive check of all case cases.
 *
 *  switch (prop) {
 *    // ...
 *    default:
 *      throw new UnreachableCaseError(prop);
 *  }
 */
export class UnreachableCaseError extends Error {
  constructor(value: never, message?: string) {
    super(message ?? `Internal error - Unreachable case: ${value}`);
  }
}

export const makeNumbersArray = (length: number) =>
  new Array(length).fill(0).map((_, index) => index + 1);
